import React, { useState } from 'react';
import axios from 'axios';
import JournalEntryPanel from './JournalEntryPanel';
import { IconX, IconChevron, IconHeart } from './Icons';
import { Fragrance, JournalEntry } from '../App';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

interface JournalProps {
  userId: string;
  authToken: string;
  catalog: Fragrance[];
  journalEntries: JournalEntry[];
  onJournalUpdated: () => void;
}

const TIMES = ['morning', 'afternoon', 'evening', 'night', 'all day long'];
const SEASONS = ['spring', 'summer', 'autumn', 'winter', 'year-round'];

const OWNERSHIP_LABELS: Record<string, string> = {
  sample: 'Sample',
  bottle: 'Full Bottle',
  would_buy_again: 'Would buy again',
  sold: 'Sold',
  // legacy
  full_bottle: 'Full Bottle',
};

const Journal: React.FC<JournalProps> = ({ userId, authToken, catalog, journalEntries, onJournalUpdated }) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteLogConfirm, setDeleteLogConfirm] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Panel state: null = closed, 'new' = new session, logId = editing that log
  const [panelEntry, setPanelEntry] = useState<JournalEntry | null>(null);
  const [panelEditLog, setPanelEditLog] = useState<any>(null);

  const [fragrancePopup, setFragrancePopup] = useState<Fragrance | null>(null);

  const [search, setSearch] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [filterTime, setFilterTime] = useState('');
  const [filterOwnership, setFilterOwnership] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Get most recent wear log (for display)
  const getMostRecentLog = (entry: JournalEntry) => {
    if (entry.wearLogs && entry.wearLogs.length > 0) return entry.wearLogs[entry.wearLogs.length - 1];
    if (entry.wearLog) return entry.wearLog;
    return null;
  };

  // Effective rating: most recent log's rating or entry-level rating (for old data)
  const effectiveRating = (entry: JournalEntry) => {
    const log = getMostRecentLog(entry);
    return log?.rating || entry.rating || 'neutral';
  };

  const formatDate = (isoString: string) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const formatFullDate = (isoString: string) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const ratingDisplay = (rating: string) => {
    if (rating === 'love') return { node: <span>★ ATF</span>, className: 'text-charcoal font-medium' };
    if (rating === 'like') return { node: <span className="inline-flex items-center gap-1"><IconHeart size={11} strokeWidth={1.5} /> Like</span>, className: 'text-charcoal' };
    if (rating === 'dislike') return { node: <span>✕ Dislike</span>, className: 'text-stone-light' };
    return { node: <span>~ Neutral</span>, className: 'text-stone' };
  };

  const ownershipTags = (log: any): string[] => {
    if (!log) return [];
    if (log.ownership && Array.isArray(log.ownership) && log.ownership.length > 0) {
      return log.ownership.map((o: string) => OWNERSHIP_LABELS[o] || o);
    }
    // Legacy
    const tags: string[] = [];
    if (log.ownershipStatus === 'sample') tags.push('Sample');
    if (log.ownershipStatus === 'full_bottle') tags.push('Full Bottle');
    if (log.wouldBuyFullBottle === true) tags.push('Would buy again');
    return tags;
  };

  const handleDeleteEntry = async (fragranceId: string) => {
    setDeleteError(null);
    try {
      await axios.post(`${API_URL}/journal`, { action: 'deleteEntry', userId, fragranceId }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setDeleteConfirm(null);
      setExpanded(null);
      onJournalUpdated();
    } catch (e) {
      console.error('Failed to delete:', e);
      setDeleteError('Could not delete. Please try again.');
    }
  };

  const handleDeleteWearLog = async (fragranceId: string, logId: string) => {
    setDeleteError(null);
    try {
      await axios.post(`${API_URL}/journal`, { action: 'deleteWearLog', userId, fragranceId, logId }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setDeleteLogConfirm(null);
      onJournalUpdated();
    } catch (e) {
      console.error('Failed to delete wear log:', e);
      setDeleteError('Could not delete wear log. Please try again.');
    }
  };

  const lookupCatalogFragrance = (fragranceId: string): Fragrance | undefined =>
    catalog.find(f => f.fragranceId === fragranceId);

  const filteredEntries = journalEntries
    .filter(entry => {
      const log = getMostRecentLog(entry);
      const rating = effectiveRating(entry);
      const q = search.toLowerCase();
      const matchSearch = !search ||
        entry.fragranceName?.toLowerCase().includes(q) ||
        entry.fragranceHouse?.toLowerCase().includes(q) ||
        log?.notes?.toLowerCase().includes(q);
      const matchSeason = !filterSeason || log?.season?.toLowerCase().includes(filterSeason);
      const matchTime = !filterTime || log?.timeOfDay?.toLowerCase().includes(filterTime);
      const matchOwnership = !filterOwnership || (
        (log?.ownership || []).includes(filterOwnership) ||
        entry.ownershipStatus === filterOwnership
      );
      const matchRating = !filterRating || rating === filterRating;
      return matchSearch && matchSeason && matchTime && matchOwnership && matchRating;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  if (journalEntries.length === 0) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-serif text-3xl text-charcoal mb-4">Your journal is empty.</p>
          <p className="text-stone mb-2">Search for a fragrance in Discover</p>
          <p className="text-stone-light text-sm">and tap the pencil icon to write your first entry.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-cream px-6 py-10 pb-24 md:pb-10">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-baseline mb-8">
            <h2 className="font-serif text-3xl text-charcoal">My Journal</h2>
            <p className="text-stone-light text-sm">{journalEntries.length} {journalEntries.length === 1 ? 'fragrance' : 'fragrances'}</p>
          </div>

          {/* Filters */}
          <div className="space-y-3 mb-8">
            <input
              type="text"
              value={search}
              onChange={e => setSearch((e.target as HTMLInputElement).value)}
              placeholder="Search by name, house, or notes..."
              className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
            />
            <div className="flex gap-2 flex-wrap">
              {[
                { label: <span>★ All-time Fave</span>, value: 'love' },
                { label: <span className="inline-flex items-center gap-1"><IconHeart size={11} strokeWidth={1.5} /> Liked</span>, value: 'like' },
                { label: <span>~ Neutral</span>, value: 'neutral' },
                { label: <span>✕ Disliked</span>, value: 'dislike' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setFilterRating(filterRating === opt.value ? '' : opt.value)}
                  className={`px-3 py-1 text-xs tracking-widest uppercase border transition-colors rounded-md ${filterRating === opt.value ? 'bg-charcoal text-cream border-charcoal' : 'bg-white text-stone border-cream-dark hover:border-stone'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              {SEASONS.map(s => (
                <button key={s}
                  onClick={() => setFilterSeason(filterSeason === s ? '' : s)}
                  className={`px-3 py-1 text-xs tracking-widest uppercase border transition-colors rounded-md ${filterSeason === s ? 'bg-charcoal text-cream border-charcoal' : 'bg-white text-stone border-cream-dark hover:border-stone'}`}>
                  {s}
                </button>
              ))}
              <div className="w-px bg-cream-dark mx-1" />
              {TIMES.map(t => (
                <button key={t}
                  onClick={() => setFilterTime(filterTime === t ? '' : t)}
                  className={`px-3 py-1 text-xs tracking-widest uppercase border transition-colors rounded-md ${filterTime === t ? 'bg-charcoal text-cream border-charcoal' : 'bg-white text-stone border-cream-dark hover:border-stone'}`}>
                  {t}
                </button>
              ))}
              <div className="w-px bg-cream-dark mx-1" />
              {[
                { label: 'Sample', value: 'sample' },
                { label: 'Bottle', value: 'bottle' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setFilterOwnership(filterOwnership === opt.value ? '' : opt.value)}
                  className={`px-3 py-1 text-xs tracking-widest uppercase border transition-colors rounded-md ${filterOwnership === opt.value ? 'bg-charcoal text-cream border-charcoal' : 'bg-white text-stone border-cream-dark hover:border-stone'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-4">
              <button onClick={() => setSortOrder('newest')}
                className={`text-xs tracking-widest uppercase transition-colors pb-1 ${sortOrder === 'newest' ? 'text-charcoal border-b border-charcoal' : 'text-stone-light hover:text-stone'}`}>
                Most recent
              </button>
              <button onClick={() => setSortOrder('oldest')}
                className={`text-xs tracking-widest uppercase transition-colors pb-1 ${sortOrder === 'oldest' ? 'text-charcoal border-b border-charcoal' : 'text-stone-light hover:text-stone'}`}>
                Oldest first
              </button>
            </div>
          </div>

          <p className="text-stone-light text-xs tracking-widest uppercase mb-4">
            {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
          </p>

          <div className="space-y-3">
            {filteredEntries.map(entry => {
              const isExpanded = expanded === entry.fragranceId;
              const mostRecentLog = getMostRecentLog(entry);
              const rating = effectiveRating(entry);
              const { node: ratingNode, className: ratingClass } = ratingDisplay(rating);
              const catalogFrag = lookupCatalogFragrance(entry.fragranceId);
              const wearLogs = entry.wearLogs || (entry.wearLog ? [entry.wearLog] : []);
              const pastLogs = wearLogs.slice(0, -1).reverse(); // all except most recent, newest first

              return (
                <div key={entry.fragranceId} className="bg-white border border-cream-dark rounded-xl">

                  {/* Collapsed header */}
                  <button
                    onClick={() => {
                      setExpanded(isExpanded ? null : entry.fragranceId);
                      setDeleteConfirm(null);
                      setDeleteLogConfirm(null);
                      setExpandedLogId(null);
                    }}
                    className="w-full p-5 text-left flex justify-between items-start"
                  >
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={e => { e.stopPropagation(); if (catalogFrag) setFragrancePopup(catalogFrag); }}
                        className={`font-serif text-lg text-charcoal text-left ${catalogFrag ? 'hover:text-stone transition-colors' : ''}`}
                      >
                        {entry.fragranceName}
                      </button>
                      <p className="text-stone-light text-sm">{entry.fragranceHouse || ''}</p>
                      <p className={`text-xs tracking-widest uppercase mt-1.5 flex items-center gap-1 ${ratingClass}`}>
                        {ratingNode}
                        {mostRecentLog?.date && <span className="text-stone-light">· {formatDate(mostRecentLog.date || entry.updatedAt || entry.createdAt)}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      {wearLogs.length > 0 && (
                        <span className="text-xs text-stone-light tracking-widest uppercase">{wearLogs.length} {wearLogs.length === 1 ? 'wear' : 'wears'}</span>
                      )}
                      <IconChevron size={14} direction={isExpanded ? 'up' : 'down'} className="text-stone-light" />
                    </div>
                  </button>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className="border-t border-cream-dark">

                      {/* Most recent wear log */}
                      {mostRecentLog ? (
                        <div className="px-5 pt-4 pb-2">
                          <div className="flex justify-between items-center mb-3">
                            <p className="text-xs tracking-widest uppercase text-stone-light">
                              {formatFullDate(mostRecentLog.date || entry.updatedAt || entry.createdAt)}
                              {wearLogs.length > 0 && <span className="ml-2 text-stone-light">· Most recent</span>}
                            </p>
                            <div className="flex gap-4">
                              <button
                                onClick={() => { setPanelEntry(entry); setPanelEditLog(mostRecentLog); }}
                                className="text-xs tracking-widest uppercase text-stone hover:text-charcoal transition-colors"
                              >Edit</button>
                              {deleteLogConfirm === mostRecentLog.logId ? (
                                <span className="flex gap-3">
                                  <button onClick={() => handleDeleteWearLog(entry.fragranceId, mostRecentLog.logId)} className="text-xs tracking-widest uppercase text-red-400">Confirm</button>
                                  <button onClick={() => setDeleteLogConfirm(null)} className="text-xs tracking-widest uppercase text-stone-light">Cancel</button>
                                </span>
                              ) : (
                                <button
                                  onClick={() => setDeleteLogConfirm(mostRecentLog.logId)}
                                  className="text-xs tracking-widest uppercase text-stone-light hover:text-red-400 transition-colors"
                                >Delete</button>
                              )}
                            </div>
                          </div>

                          {/* Rating badge */}
                          <div className={`inline-flex items-center gap-1.5 text-xs tracking-widest uppercase mb-3 ${ratingClass}`}>
                            {ratingNode}
                          </div>

                          {/* Ownership tags */}
                          {ownershipTags(mostRecentLog).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {ownershipTags(mostRecentLog).map(tag => (
                                <span key={tag} className="text-xs bg-cream-dark text-stone px-3 py-1 rounded-full tracking-wide">{tag}</span>
                              ))}
                            </div>
                          )}

                          {/* Time / Season pills */}
                          {(mostRecentLog.timeOfDay || mostRecentLog.season) && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {mostRecentLog.timeOfDay && (
                                <span className="text-xs text-stone-light tracking-widest uppercase">{mostRecentLog.timeOfDay}</span>
                              )}
                              {mostRecentLog.timeOfDay && mostRecentLog.season && <span className="text-stone-light text-xs">·</span>}
                              {mostRecentLog.season && (
                                <span className="text-xs text-stone-light tracking-widest uppercase">{mostRecentLog.season}</span>
                              )}
                            </div>
                          )}

                          {/* Impression fields */}
                          {mostRecentLog.topNotesImpression && (
                            <div className="mb-2">
                              <p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">First impression</p>
                              <p className="text-charcoal text-sm">{mostRecentLog.topNotesImpression}</p>
                            </div>
                          )}
                          {mostRecentLog.heartNotesImpression && (
                            <div className="mb-2">
                              <p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">After an hour</p>
                              <p className="text-charcoal text-sm">{mostRecentLog.heartNotesImpression}</p>
                            </div>
                          )}
                          {mostRecentLog.baseNotesImpression && (
                            <div className="mb-2">
                              <p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">End of day</p>
                              <p className="text-charcoal text-sm">{mostRecentLog.baseNotesImpression}</p>
                            </div>
                          )}
                          {mostRecentLog.notes && (
                            <div className="mb-2">
                              <p className="text-xs tracking-widest uppercase text-stone-light mb-0.5">Notes</p>
                              <p className="text-charcoal text-sm">{mostRecentLog.notes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="px-5 pt-4 pb-2">
                          <p className="text-stone-light text-sm mb-3">No details recorded.</p>
                        </div>
                      )}

                      {/* Log new wear session */}
                      <div className="px-5 py-3">
                        <button
                          onClick={() => { setPanelEntry(entry); setPanelEditLog(null); }}
                          className="w-full border border-cream-dark rounded-xl py-3 text-xs tracking-widest uppercase text-stone-light hover:border-stone hover:text-charcoal transition-colors"
                        >
                          + Log a new wear session
                        </button>
                      </div>

                      {/* Wear history timeline */}
                      {pastLogs.length > 0 && (
                        <div className="border-t border-cream-dark">
                          <p className="px-5 pt-3 pb-2 text-xs tracking-widest uppercase text-stone-light">Wear history</p>
                          {pastLogs.map((log: any) => {
                            const logRating = log.rating || entry.rating || 'neutral';
                            const { node: logRatingNode, className: logRatingClass } = ratingDisplay(logRating);
                            const isLogExpanded = expandedLogId === log.logId;
                            return (
                              <div key={log.logId} className="border-t border-cream-dark">
                                <button
                                  onClick={() => setExpandedLogId(isLogExpanded ? null : log.logId)}
                                  className="w-full px-5 py-3 flex justify-between items-center text-left"
                                >
                                  <span className="text-xs tracking-widest uppercase text-stone-light">
                                    {formatFullDate(log.date || '')}
                                  </span>
                                  <div className="flex items-center gap-3">
                                    <span className={`text-xs tracking-widests uppercase flex items-center gap-1 ${logRatingClass}`}>{logRatingNode}</span>
                                    <IconChevron size={11} direction={isLogExpanded ? 'up' : 'down'} className="text-stone-light" />
                                  </div>
                                </button>

                                {isLogExpanded && (
                                  <div className="px-5 pb-4 bg-cream bg-opacity-40">
                                    {ownershipTags(log).length > 0 && (
                                      <div className="flex flex-wrap gap-2 mb-2">
                                        {ownershipTags(log).map((tag: string) => (
                                          <span key={tag} className="text-xs bg-cream-dark text-stone px-3 py-1 rounded-full tracking-wide">{tag}</span>
                                        ))}
                                      </div>
                                    )}
                                    {(log.timeOfDay || log.season) && (
                                      <p className="text-xs text-stone-light tracking-widest uppercase mb-2">
                                        {[log.timeOfDay, log.season].filter(Boolean).join(' · ')}
                                      </p>
                                    )}
                                    {log.topNotesImpression && <p className="text-sm text-charcoal mb-1">{log.topNotesImpression}</p>}
                                    {log.heartNotesImpression && <p className="text-sm text-charcoal mb-1">{log.heartNotesImpression}</p>}
                                    {log.baseNotesImpression && <p className="text-sm text-charcoal mb-1">{log.baseNotesImpression}</p>}
                                    {log.notes && <p className="text-sm text-charcoal mb-2">{log.notes}</p>}
                                    <div className="flex gap-4 mt-2">
                                      <button
                                        onClick={() => { setPanelEntry(entry); setPanelEditLog(log); }}
                                        className="text-xs tracking-widest uppercase text-stone hover:text-charcoal transition-colors"
                                      >Edit</button>
                                      {deleteLogConfirm === log.logId ? (
                                        <span className="flex gap-3">
                                          <button onClick={() => handleDeleteWearLog(entry.fragranceId, log.logId)} className="text-xs tracking-widest uppercase text-red-400">Confirm</button>
                                          <button onClick={() => setDeleteLogConfirm(null)} className="text-xs tracking-widest uppercase text-stone-light">Cancel</button>
                                        </span>
                                      ) : (
                                        <button onClick={() => setDeleteLogConfirm(log.logId)} className="text-xs tracking-widest uppercase text-stone-light hover:text-red-400 transition-colors">Delete</button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Delete entire entry */}
                      <div className="border-t border-cream-dark px-5 py-3">
                        {deleteError && (deleteConfirm === entry.fragranceId || deleteLogConfirm) && (
                          <p className="text-xs text-red-400 mb-1">{deleteError}</p>
                        )}
                        {deleteConfirm === entry.fragranceId ? (
                          <div className="flex gap-4 items-center">
                            <p className="text-xs text-charcoal">Remove {entry.fragranceName} entirely?</p>
                            <button onClick={() => handleDeleteEntry(entry.fragranceId)} className="text-xs tracking-widest uppercase text-red-400 hover:text-red-600">Yes, remove</button>
                            <button onClick={() => setDeleteConfirm(null)} className="text-xs tracking-widest uppercase text-stone-light hover:text-charcoal">Cancel</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(entry.fragranceId)}
                            className="text-xs tracking-widest uppercase text-stone-light hover:text-red-400 transition-colors"
                          >
                            Remove from journal
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fragrance info popup */}
      {fragrancePopup && (
        <>
          <div className="fixed inset-0 bg-charcoal bg-opacity-40 z-40" onClick={() => setFragrancePopup(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cream z-50 w-96 rounded-xl max-w-[92vw] p-6 shadow-lg max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="font-serif text-2xl text-charcoal">{fragrancePopup.name}</p>
                <p className="text-stone-light text-sm">{fragrancePopup.house}{fragrancePopup.year ? ` · ${fragrancePopup.year}` : ''}</p>
              </div>
              <button onClick={() => setFragrancePopup(null)} className="text-stone-light hover:text-charcoal ml-4 flex-shrink-0"><IconX size={16} /></button>
            </div>
            {fragrancePopup.description && <p className="text-stone text-sm leading-relaxed mb-4">{fragrancePopup.description}</p>}
            {fragrancePopup.topNotes && fragrancePopup.topNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Top notes</p>
                <div className="flex flex-wrap gap-2">{fragrancePopup.topNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}</div>
              </div>
            )}
            {fragrancePopup.middleNotes && fragrancePopup.middleNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Heart notes</p>
                <div className="flex flex-wrap gap-2">{fragrancePopup.middleNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}</div>
              </div>
            )}
            {fragrancePopup.baseNotes && fragrancePopup.baseNotes.length > 0 && (
              <div className="mb-3">
                <p className="text-xs tracking-widest uppercase text-stone-light mb-2">Base notes</p>
                <div className="flex flex-wrap gap-2">{fragrancePopup.baseNotes.map((n, i) => <span key={i} className="text-xs bg-cream-dark text-stone px-3 py-1">{n}</span>)}</div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Journal entry panel */}
      {panelEntry && (
        <JournalEntryPanel
          fragrance={{
            fragranceId: panelEntry.fragranceId,
            name: panelEntry.fragranceName,
            house: panelEntry.fragranceHouse || '',
            ...(() => { const f = catalog.find(c => c.fragranceId === panelEntry.fragranceId); return f ? { notes: f.notes, topNotes: f.topNotes, middleNotes: f.middleNotes, baseNotes: f.baseNotes } : {}; })(),
          }}
          userId={userId}
          authToken={authToken}
          existingEntry={panelEntry}
          editLog={panelEditLog}
          onClose={() => { setPanelEntry(null); setPanelEditLog(null); }}
          onSaved={() => {
            onJournalUpdated();
            setPanelEntry(null);
            setPanelEditLog(null);
          }}
        />
      )}
    </>
  );
};

export default Journal;
